import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { PadlockService } from "../../padlock/padlock.service";
import { Member, Role } from "../state/members.reducer";

export class MemberRoleAssignmentError extends Error {
  constructor(
    readonly member: Member,
    readonly roleID: string,
    cause: unknown,
  ) {
    super("The member was added, but the admin role could not be assigned.", {
      cause,
    });
  }
}

@Injectable({ providedIn: "root" })
export class WorkspaceMembersService {
  constructor(private padlock: PadlockService) {}

  getWorkspaceMembers(): Promise<Member[]> {
    return firstValueFrom(this.padlock.getUsers());
  }

  getWorkspaceRoles(): Promise<Role[]> {
    return firstValueFrom(this.padlock.getRoles());
  }

  async removeUserFromWorkspace(
    tenantID: string,
    userID: string,
  ): Promise<void> {
    await firstValueFrom(this.padlock.removeUserFromTenant(tenantID, userID));
  }

  async assignRole(userID: string, roleID: string): Promise<void> {
    await firstValueFrom(this.padlock.addUserRole(userID, roleID));
  }

  async addUserToWorkspaceByEmail(
    tenantID: string,
    roleID: string,
    email: string,
  ): Promise<Member> {
    const response = await firstValueFrom(
      this.padlock.addUserToTenantByEmail(tenantID, email),
    );
    const member: Member = { email, uuid: response.user_uuid };

    try {
      await this.assignRole(member.uuid, roleID);
    } catch (error: unknown) {
      throw new MemberRoleAssignmentError(member, roleID, error);
    }

    return member;
  }
}
